import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Target, User, Activity, RefreshCw, Footprints, Edit3, Check, X, Camera, Ruler, Dumbbell, Trash2, Pencil, Plus, ChevronDown, ChevronRight, Brain, Users, Crown, Bell, FileText, MessageCircle, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WeightEntrySheet } from '@/components/WeightEntrySheet';
import { monthChange, type WeightPoint } from '@/lib/weight';
import { useBodyClass } from '@/hooks/useBodyClass';
import { STYLE_TEXT, retestDue } from '@/lib/eatingProfile';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { useUserStore } from '@/store/userStore';
import { ProfileGroup } from '@/components/ProfileGroup';
import { EatingTestCard } from '@/components/EatingTestCard';
import { SHOW_WORKOUTS } from '@/lib/features';
import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import { buildDailyTargets } from '@/lib/tdee';
import { useTelegram } from '@/hooks/useTelegram';
import { useTodaySteps, logSteps } from '@/hooks/useSteps';
import { goalLabel, activityLabel, fmtNum, getActiveTargets } from '@/utils/format';
import { calculateBodyFatNavy } from '@/utils/format';
import { calculateBMR, ageFromBirthDate } from '@/lib/tdee';
import { useMeasurements, addMeasurement, daysFromLast } from '@/hooks/useMeasurements';
import { useTodayActivity, addActivity, deleteActivity, type ActivityEntry } from '@/hooks/useActivity';
import { EditActivityEntryModal } from '@/components/EditActivityEntryModal';
import { format, subDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AchievementModal } from '@/components/AchievementModal';
import { GoalsCard } from '@/components/GoalsCard';
import { EditParamsModal, EditGoalModal, EditActivityModal } from '@/components/ProfileEditModals';
import { LegalDocumentsCard } from '@/components/LegalDocumentsCard';
import { PartnersCard } from '@/components/PartnersCard';
import { MealPhotosCard } from '@/components/MealPhotosCard';
import { NotificationSettingsCard } from '@/components/NotificationSettingsCard';
// 🆕 Хелпер для выбора даты — возвращает 'yyyy-MM-dd'
type DateOption = 'today' | 'yesterday' | 'day_before';
function dateOptionToString(opt: DateOption): string {
  const now = new Date();
  if (opt === 'today') return format(now, 'yyyy-MM-dd');
  if (opt === 'yesterday') return format(subDays(now, 1), 'yyyy-MM-dd');
  return format(subDays(now, 2), 'yyyy-MM-dd');
}
function dateOptionLabel(opt: DateOption): string {
  if (opt === 'today') return 'Сегодня';
  if (opt === 'yesterday') return 'Вчера';
  return 'Позавчера';
}
// 🆕 Компонент-переключатель даты
function DateSelector({ value, onChange }: { value: DateOption; onChange: (v: DateOption) => void }) {
  const options: DateOption[] = ['today', 'yesterday', 'day_before'];
  return (
    <div className="flex gap-1 p-1 bg-bg-elevated rounded-lg mb-3">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`flex-1 py-1.5 px-2 rounded-md text-[11px] font-medium transition-all ${
            value === opt
              ? 'bg-bg text-ink shadow-sm'
              : 'text-ink-muted'
          }`}
        >
          {dateOptionLabel(opt)}
        </button>
      ))}
    </div>
  );
}
export default function Profile() {
  const nav = useNavigate();
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [editingTargets, setEditingTargets] = useState(false);
  const [customCal, setCustomCal] = useState('');
  const [customProt, setCustomProt] = useState('');
  const [customFat, setCustomFat] = useState('');
  const [customCarbs, setCustomCarbs] = useState('');
  const [savingTargets, setSavingTargets] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editParamsOpen, setEditParamsOpen] = useState(false);
  const [editGoalOpen, setEditGoalOpen] = useState(false);
  const [editActivityOpen, setEditActivityOpen] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);
  const [normOpen, setNormOpen] = useState(false);
  const [weights, setWeights] = useState<WeightPoint[] | null>(null);
  useBodyClass('modal-open', normOpen);

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('weight_log')
      .select('weight_kg, logged_at')
      .eq('user_id', profile.id)
      .order('logged_at', { ascending: false })
      .limit(60)
      .then(({ data }) => setWeights((data as WeightPoint[]) ?? []));
  }, [profile?.id, profile?.weight_kg]);

  if (!profile) return null;
  const targets = getActiveTargets(profile);
  function startEditTargets() {
    setCustomCal(String(targets.calories));
    setCustomProt(String(targets.protein));
    setCustomFat(String(targets.fat));
    setCustomCarbs(String(targets.carbs));
    setEditingTargets(true);
  }
  async function saveCustomTargets() {
    if (!profile) return;
    setSavingTargets(true);
    try {
      if (!(await requireSession())) return;
      const update = {
        custom_calories: Number(customCal),
        custom_protein_g: Number(customProt),
        custom_fat_g: Number(customFat),
        custom_carbs_g: Number(customCarbs),
        use_custom_targets: true,
      };
      const { data, error } = await supabase
        .from('profiles')
        .update(update)
        .eq('id', profile.id)
        .select()
        .single();
      if (error) throw error;
      setProfile(data);
      setEditingTargets(false);
      haptic('success');
    } catch (e) {
      haptic('error');
    } finally {
      setSavingTargets(false);
    }
  }
  async function resetToAutoTargets() {
    if (!profile) return;
    if (!(await requireSession())) return;
    const { data, error } = await supabase
      .from('profiles')
      .update({ use_custom_targets: false })
      .eq('id', profile.id)
      .select()
      .single();
    if (!error) {
      setProfile(data);
      setEditingTargets(false);
      haptic('success');
    }
  }
  async function uploadAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    if (!file.type.startsWith('image/')) {
      alert('Выбери картинку');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Файл слишком большой (макс 5MB)');
      return;
    }
    setUploadingAvatar(true);
    haptic('medium');
    try {
      if (!(await requireSession())) return;
      const ext = file.name.split('.').pop() || 'jpg';
      const filePath = `${profile.id}/avatar.${ext}`;
      await supabase.storage.from('avatars').remove([
        `${profile.id}/avatar.jpg`,
        `${profile.id}/avatar.jpeg`,
        `${profile.id}/avatar.png`,
        `${profile.id}/avatar.webp`,
      ]);
      const { error: uploadErr } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { data, error } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id)
        .select()
        .single();
      if (error) throw error;
      setProfile(data);
      haptic('success');
    } catch (err) {
      alert('Не удалось загрузить фото. Попробуй ещё раз.');
      haptic('error');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }
  async function logout() {
    haptic('medium');
    await supabase.auth.signOut();
    location.reload();
  }

  // ── Вариант Б (14.10.2026) ──
  // Было: семнадцать карточек одна под другой, ввод шагов дублировал главную,
  // вес жил отдельной карточкой «Обновить вес», норма — большой карточкой сверху.
  // Стало: шапка в одну строку, три плитки с цифрами (вес, изменение за месяц,
  // норма) и три коротких списка: Я · Вместе · Приложение. Шаги вводятся только
  // на главной — там же есть «вчера» и «позавчера». Ввод веса — «+» на плитке,
  // то же окно открывает напоминание на главной.
  const change = weights ? monthChange(weights) : null;
  const goalDir = profile.target_weight_kg && profile.weight_kg
    ? Math.sign(profile.target_weight_kg - profile.weight_kg)
    : 0;
  const changeGood = change && goalDir !== 0 ? Math.sign(change.delta) === goalDir : null;
  const ep = profile.eating_profile ?? null;
  const testDue = retestDue(ep);
  const sub = subscriptionState(profile);

  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      {/* Шапка */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative shrink-0">
          <Avatar
            url={profile.avatar_url}
            name={profile.first_name}
            size="lg"
            className={uploadingAvatar ? 'opacity-50' : ''}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAvatar}
            aria-label="Сменить фото"
            className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-emerald-glow text-bg flex items-center justify-center shadow-lg active:scale-95 transition-all"
          >
            <Camera size={11} />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg leading-tight truncate">{profile.first_name}</div>
          <div className="text-[12px] text-ink-dim flex items-center gap-1.5 flex-wrap mt-0.5">
            {profile.tg_username && <span className="truncate">@{profile.tg_username}</span>}
            {(profile.current_streak_meals ?? 0) > 0 && (
              <span className="text-warn">🔥 {profile.current_streak_meals} {plural(profile.current_streak_meals ?? 0, 'день', 'дня', 'дней')}</span>
            )}
          </div>
        </div>
        <button
          onClick={() => nav('/paywall')}
          className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium ${sub.chipClass}`}
        >
          {sub.chip}
        </button>
      </div>

      {/* Три плитки */}
      <div className="grid grid-cols-3 gap-2 mb-6">
        <div className="card p-3 relative">
          <div className="text-[10px] text-ink-dim uppercase tracking-wider">Вес</div>
          <div className="font-display text-xl font-bold mt-1 leading-none">
            {profile.weight_kg ?? '—'}<span className="text-xs text-ink-muted font-body ml-0.5">кг</span>
          </div>
          <div className="text-[10px] text-ink-dim mt-1.5 truncate">
            {profile.target_weight_kg ? `цель ${profile.target_weight_kg}` : 'без цели'}
          </div>
          <button
            onClick={() => { setWeightOpen(true); haptic('light'); }}
            aria-label="Внести вес"
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-sky-glow text-bg flex items-center justify-center active:scale-90 transition-transform"
          >
            <Plus size={15} strokeWidth={3} />
          </button>
        </div>
        <div className="card p-3">
          <div className="text-[10px] text-ink-dim uppercase tracking-wider">За месяц</div>
          {change ? (
            <>
              <div className={`font-display text-xl font-bold mt-1 leading-none ${
                changeGood === true ? 'text-emerald-glow' : changeGood === false ? 'text-warn' : ''
              }`}>
                {change.delta > 0 ? '+' : change.delta < 0 ? '−' : ''}{Math.abs(change.delta)}
                <span className="text-xs text-ink-muted font-body ml-0.5">кг</span>
              </div>
              <div className="text-[10px] text-ink-dim mt-1.5">за {change.days} {plural(change.days, 'день', 'дня', 'дней')}</div>
            </>
          ) : (
            <>
              <div className="font-display text-xl font-bold mt-1 leading-none text-ink-dim">—</div>
              <div className="text-[10px] text-ink-dim mt-1.5 leading-tight">
                {weights === null ? '…' : 'нужно 2 замера'}
              </div>
            </>
          )}
        </div>
        <button
          onClick={() => { setNormOpen(true); haptic('light'); }}
          className="card p-3 text-left active:scale-[0.98] transition-transform"
        >
          <div className="text-[10px] text-ink-dim uppercase tracking-wider">Норма</div>
          <div className="font-display text-xl font-bold mt-1 leading-none text-emerald-glow">
            {fmtNum(targets.calories)}
          </div>
          <div className="text-[10px] text-ink-dim mt-1.5 truncate">
            ккал{profile.use_custom_targets ? ' · своя' : ''} ›
          </div>
        </button>
      </div>

      {/* Цель по тренировкам и ввод активности — только когда раздел открыт
          (тренировки скрыты флагом с 29.08.2026). */}
      {SHOW_WORKOUTS && <GoalsCard />}
      {SHOW_WORKOUTS && <ActivityCard />}

      <SectionTitle>Я</SectionTitle>
      <div className="space-y-1.5 mb-6">
        <ExpandRow
          icon={<User size={16} className="text-emerald-glow" />}
          title="О себе"
          subtitle={`${profile.height_cm ?? '—'} см · ${goalLabel[profile.goal!] ?? 'цель не задана'} · ${activityLabel[profile.activity_level!] ?? '—'}`}
        >
          <div className="space-y-1.5">
            <InfoRow Icon={User} label="Параметры" value={`${profile.height_cm} см · ${profile.weight_kg} кг`} onClick={() => setEditParamsOpen(true)} />
            <InfoRow Icon={Target} label="Цель" value={goalLabel[profile.goal!] ?? '—'} onClick={() => setEditGoalOpen(true)} />
            <InfoRow Icon={Activity} label="Активность" value={activityLabel[profile.activity_level!] ?? '—'} onClick={() => setEditActivityOpen(true)} />
          </div>
        </ExpandRow>
        <ExpandRow
          icon={<Ruler size={16} className="text-emerald-glow" />}
          title="Замеры тела"
          subtitle="Шея, талия, бёдра · % жира"
        >
          <MeasurementsCard />
        </ExpandRow>
        <NavRow
          icon={<Brain size={16} className="text-emerald-glow" />}
          title="Тест пищевого поведения"
          subtitle={!ep ? 'Что влияет на то, как ты ешь' : testDue ? 'Прошло 2 месяца — пройди снова' : STYLE_TEXT[ep.style].title}
          accent={testDue}
          onClick={() => nav('/test')}
        />
        {ep && (
          <NavRow
            icon={<Sparkles size={16} className="text-emerald-glow" />}
            title="Расширенный портрет"
            subtitle="Бонус к подписке: три стороны и твой ритм"
            onClick={() => nav('/portrait')}
          />
        )}
      </div>

      <SectionTitle>Вместе</SectionTitle>
      <div className="space-y-1.5 mb-6">
        <ExpandRow
          icon={<Users size={16} className="text-sky-glow" />}
          title="Едим вместе"
          subtitle="Отправлять друг другу еду в дневник"
        >
          <PartnersCard />
        </ExpandRow>
        <NavRow
          icon={<span className="text-[15px] leading-none">🎁</span>}
          title="Пригласить друга"
          subtitle="Другу +7 дней, тебе +30 за его оплату"
          onClick={() => nav('/invite')}
        />
        <NavRow
          icon={<span className="text-[15px] leading-none">💛</span>}
          title="Подарить заботу"
          subtitle="Оплатить подписку близкому"
          onClick={() => nav('/gift')}
        />
      </div>

      <SectionTitle>Приложение</SectionTitle>
      <div className="space-y-1.5 mb-6">
        <NavRow
          icon={<Crown size={16} className="text-warn" />}
          title="Подписка"
          subtitle={sub.line}
          accent={sub.urgent}
          onClick={() => nav('/paywall')}
        />
        <ExpandRow icon={<Bell size={16} className="text-ink-muted" />} title="Уведомления" subtitle="Что и когда присылать в бот">
          <NotificationSettingsCard />
          <MealPhotosCard />
        </ExpandRow>
        <ExpandRow icon={<FileText size={16} className="text-ink-muted" />} title="Аккаунт и документы" subtitle="Вход, оферта, политика">
          <ConnectedAccounts />
          <LegalDocumentsCard />
        </ExpandRow>
        <ExpandRow icon={<MessageCircle size={16} className="text-ink-muted" />} title="Поддержка" subtitle="Новости и вопросы">
          <SupportCard />
        </ExpandRow>
      </div>

      <AdminLink profile={profile} />
      <button
        onClick={logout}
        className="w-full py-3 text-sm text-ink-dim hover:text-danger transition-colors flex items-center justify-center gap-2"
      >
        <LogOut size={14} /> Выйти
      </button>
      <p className="text-center text-[10px] text-ink-dim/70 leading-relaxed px-4 mt-4 mb-3">
        ℹ️ Все расчёты, нормы и советы в приложении носят информационный характер и не являются медицинской рекомендацией. Свои ограничения по здоровью знаете вы и ваш лечащий врач.
      </p>
      <div className="text-center mt-4 mb-2">
        <div className="text-[11px] text-ink-dim font-medium">Slava Health</div>
        <div className="text-[10px] text-ink-dim/70 mt-0.5">Помощник по питанию · by Kokorin Health</div>
      </div>

      {/* Модалки */}
      <EditParamsModal open={editParamsOpen} onClose={() => setEditParamsOpen(false)} />
      <EditGoalModal open={editGoalOpen} onClose={() => setEditGoalOpen(false)} />
      <EditActivityModal open={editActivityOpen} onClose={() => setEditActivityOpen(false)} />
      <WeightEntrySheet open={weightOpen} onClose={() => setWeightOpen(false)} />

      {/* Норма — окном по тапу на плитку: сюда же переехал базальный метаболизм */}
      <AnimatePresence>
        {normOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => { setNormOpen(false); setEditingTargets(false); }}
              className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto px-5 pt-4 max-h-[88dvh] overflow-y-auto"
              style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <div className="w-10 h-1 rounded-full bg-line mx-auto mb-4" />
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target size={16} className="text-emerald-glow" />
                  <span className="font-display font-semibold">Дневная норма</span>
                  {profile.use_custom_targets && (
                    <span className="text-[10px] bg-sky-glow/20 text-sky-glow px-2 py-0.5 rounded-full">своя</span>
                  )}
                </div>
                {!editingTargets ? (
                  <button onClick={startEditTargets} className="text-xs text-ink-dim flex items-center gap-1">
                    <Edit3 size={13} /> изменить
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={saveCustomTargets} disabled={savingTargets} className="text-emerald-glow"><Check size={18} /></button>
                    <button onClick={() => setEditingTargets(false)} className="text-ink-dim"><X size={18} /></button>
                  </div>
                )}
              </div>
              {!editingTargets ? (
                <>
                  <div className="font-display text-3xl font-bold text-emerald-glow mb-3">
                    {fmtNum(targets.calories)}
                    <span className="text-base text-ink-muted font-body ml-1">ккал/день</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                    <div><div className="label">Белки</div><div className="text-emerald-glow font-semibold">{targets.protein}г</div></div>
                    <div><div className="label">Жиры</div><div className="text-warn font-semibold">{targets.fat}г</div></div>
                    <div><div className="label">Углеводы</div><div className="text-sky-glow font-semibold">{targets.carbs}г</div></div>
                  </div>
                  <div className="text-[11px] text-ink-dim mb-3">
                    {profile.use_custom_targets
                      ? 'Норма задана вручную и не меняется при взвешивании.'
                      : 'Считается от веса, роста, возраста, цели и активности — после взвешивания пересчитается сама.'}
                  </div>
                  {profile.use_custom_targets && (
                    <button onClick={resetToAutoTargets} className="mb-3 text-xs text-ink-dim underline">
                      Вернуть автоматический расчёт
                    </button>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div><label className="label mb-1 block">Калории</label><input type="number" className="input text-sm py-2" value={customCal} onChange={(e) => setCustomCal(e.target.value)} /></div>
                  <div><label className="label mb-1 block">Белки, г</label><input type="number" className="input text-sm py-2" value={customProt} onChange={(e) => setCustomProt(e.target.value)} /></div>
                  <div><label className="label mb-1 block">Жиры, г</label><input type="number" className="input text-sm py-2" value={customFat} onChange={(e) => setCustomFat(e.target.value)} /></div>
                  <div><label className="label mb-1 block">Углеводы, г</label><input type="number" className="input text-sm py-2" value={customCarbs} onChange={(e) => setCustomCarbs(e.target.value)} /></div>
                </div>
              )}
              <BMRCard profile={profile} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Статус подписки для шапки и строки «Подписка». «Продлить» громко — только
 *  за 7 дней до конца (правило 03.09: громкость по сроку). */
function subscriptionState(profile: any): { chip: string; chipClass: string; line: string; urgent: boolean } {
  const expiresAt = profile.subscription_expires_at ? new Date(profile.subscription_expires_at) : null;
  const expired = expiresAt ? expiresAt < new Date() : false;
  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / 86400000) : 0;
  const until = expiresAt ? format(expiresAt, 'd MMMM', { locale: ru }) : '';
  if (profile.subscription_status === 'active' && !expired) {
    const soon = !!expiresAt && daysLeft <= 7;
    return soon
      ? { chip: 'Продлить', chipClass: 'bg-warn/20 text-warn', line: `Заканчивается через ${daysLeft} ${plural(daysLeft, 'день', 'дня', 'дней')}`, urgent: true }
      : { chip: 'Premium', chipClass: 'bg-emerald-glow/15 text-emerald-glow', line: expiresAt ? `Premium до ${until}` : 'Premium', urgent: false };
  }
  if (profile.subscription_status === 'trial' && !expired) {
    return { chip: `Пробный · ${daysLeft} дн.`, chipClass: 'bg-warn/20 text-warn', line: 'Первый месяц — 990 ₽', urgent: true };
  }
  return { chip: 'Оформить', chipClass: 'bg-gradient-to-r from-emerald-glow to-sky-glow text-bg font-bold', line: 'Подписка не активна', urgent: true };
}

/** Строка списка, которая раскрывает содержимое под собой. */
function ExpandRow({ icon, title, subtitle, children }: { icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const { haptic } = useTelegram();
  return (
    <div>
      <button
        onClick={() => { setOpen((v) => !v); haptic('light'); }}
        className="w-full card px-3.5 py-3 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
      >
        <div className="w-8 h-8 rounded-xl bg-bg-elevated flex items-center justify-center shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{title}</div>
          {subtitle && <div className="text-[11px] text-ink-dim mt-0.5 truncate">{subtitle}</div>}
        </div>
        <ChevronDown size={16} className={`text-ink-dim shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-2 pb-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Строка списка, которая ведёт на другой экран. */
function NavRow({ icon, title, subtitle, accent, onClick }: { icon: React.ReactNode; title: string; subtitle?: string; accent?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full card px-3.5 py-3 flex items-center gap-3 text-left active:scale-[0.99] transition-transform ${accent ? 'border border-emerald-glow/40' : ''}`}
    >
      <div className="w-8 h-8 rounded-xl bg-bg-elevated flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {subtitle && <div className={`text-[11px] mt-0.5 truncate ${accent ? 'text-emerald-glow' : 'text-ink-dim'}`}>{subtitle}</div>}
      </div>
      <ChevronRight size={16} className="text-ink-dim shrink-0" />
    </button>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-2 mt-1">
      {children}
    </div>
  );
}

function InfoRow({ Icon, label, value, onClick }: { Icon: any; label: string; value: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`card p-3 flex items-center gap-3 w-full text-left transition-colors ${
        onClick ? 'hover:bg-bg-elevated active:scale-[0.98]' : 'cursor-default'
      }`}
    >
      <Icon size={16} className="text-ink-muted" />
      <span className="text-sm text-ink-muted flex-1">{label}</span>
      <span className="text-sm font-medium">{value}</span>
      {onClick && <span className="text-ink-dim text-xs">✏️</span>}
    </button>
  );
}
function SubscriptionBadge({ profile }: { profile: any }) {
  const nav = useNavigate();
  const status = profile.subscription_status;
  const expiresAt = profile.subscription_expires_at ? new Date(profile.subscription_expires_at) : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;
  const daysLeft = expiresAt
    ? Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;
  if (status === 'active' && !isExpired) {
    return (
      <div className="mt-2 flex flex-col items-center gap-2">
        <div className="px-3 py-1 rounded-full bg-emerald-glow/15 text-emerald-glow text-xs font-medium">
          ✓ Premium активен
        </div>
        <button
          onClick={() => nav('/paywall')}
          className="text-[11px] text-ink-dim hover:text-ink underline"
        >
          {daysLeft <= 5
            ? `Заканчивается через ${daysLeft} ${daysLeft === 1 ? 'день' : 'дней'} — продлить`
            : 'Продлить заранее'}
        </button>
      </div>
    );
  }
  if (status === 'trial' && !isExpired) {
    return (
      <div className="mt-2 flex flex-col items-center gap-2">
        <div className="px-3 py-1 rounded-full bg-warn/20 text-warn text-xs">
          Пробный период · {daysLeft} {daysLeft === 1 ? 'день' : 'дней'}
        </div>
        <button
          onClick={() => nav('/paywall')}
          className="text-[11px] text-emerald-glow hover:underline"
        >
          🎁 Оформить за 990 ₽ (первый месяц)
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={() => nav('/paywall')}
      className="mt-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-glow to-sky-glow text-bg text-xs font-bold active:scale-95 transition-all"
    >
      Оформить подписку →
    </button>
  );
}
function InviteCard() {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav('/invite')}
      className="w-full card p-4 mb-3 flex items-center gap-3 active:scale-[0.99] transition-transform text-left"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-glow to-sky-glow flex items-center justify-center shrink-0 text-lg">🎁</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Пригласи друга</div>
        <div className="text-xs text-ink-dim">Другу +7 дней, тебе +30 за оплату</div>
      </div>
      <span className="text-emerald-glow text-lg shrink-0">→</span>
    </button>
  );
}
function GiftCard() {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav('/gift')}
      className="w-full card p-4 mb-3 flex items-center gap-3 active:scale-[0.99] transition-transform text-left"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-glow to-sky-glow flex items-center justify-center shrink-0 text-lg">💛</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Подари заботу</div>
        <div className="text-xs text-ink-dim">Оплати подписку близкому человеку</div>
      </div>
      <span className="text-emerald-glow text-lg shrink-0">→</span>
    </button>
  );
}
function AdminLink({ profile }: { profile: any }) {
  const nav = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!profile?.tg_id) return;
    supabase
      .from('admins')
      .select('tg_id')
      .eq('tg_id', profile.tg_id)
      .maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [profile?.tg_id]);
  if (!isAdmin) return null;
  return (
    <button
      onClick={() => nav('/admin')}
      className="w-full py-3 mb-3 text-sm text-emerald-glow flex items-center justify-center gap-2 card"
    >
      ⚙️ Админ-панель
    </button>
  );
}
// 🆕 УПРОЩЁННЫЙ компонент — без привязки email
// Показывает только что Telegram привязан. Привязки email больше нет.
function ConnectedAccounts() {
  const { profile } = useUserStore();
  if (!profile) return null;
  
  const hasTg = !!profile.tg_id;
  
  return (
    <div className="card p-4 mb-5">
      <div className="font-display font-semibold mb-3">Подключённый аккаунт</div>
      <div className="flex items-center gap-3 py-2.5">
        <div className="w-9 h-9 rounded-xl bg-sky-glow/15 flex items-center justify-center shrink-0">
          <span className="text-base">✈️</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-ink-dim">Telegram</div>
          <div className="text-sm font-medium truncate">
            {hasTg
              ? (profile.tg_username ? '@' + profile.tg_username : 'ID ' + profile.tg_id)
              : 'не привязан'}
          </div>
        </div>
        {hasTg && (
          <span className="text-[11px] text-emerald-glow shrink-0">✓ Активен</span>
        )}
      </div>
      <p className="text-[10px] text-ink-dim leading-relaxed mt-3 pt-3 border-t border-line/40">
        💡 Slava Health работает через Telegram. Если что-то не так со входом — напиши в поддержку.
      </p>
    </div>
  );
}
function MeasurementsCard() {
  const { profile } = useUserStore();
  const { measurements, refetch } = useMeasurements();
  const { haptic } = useTelegram();
  const [showForm, setShowForm] = useState(false);
  const [neck, setNeck] = useState('');
  const [waist, setWaist] = useState('');
  const [hips, setHips] = useState('');
  const [glutes, setGlutes] = useState('');
  const [saving, setSaving] = useState(false);
  if (!profile) return null;
  const isFemale = profile.sex === 'female';
  const lastDays = daysFromLast(measurements);
  const canAdd = lastDays === null || lastDays >= 7;
  const latest = measurements[0];
  const currentBf = latest?.body_fat_percent ?? null;
  const previewBf = (() => {
    if (!neck || !waist) return null;
    if (isFemale && !hips) return null;
    return calculateBodyFatNavy({
      sex: profile.sex as 'male' | 'female',
      height_cm: profile.height_cm ?? 170,
      neck_cm: Number(neck),
      waist_cm: Number(waist),
      hips_cm: hips ? Number(hips) : undefined,
    });
  })();
  async function save() {
    if (!profile) return;
    if (!neck || !waist) {
      alert('Введи минимум шею и талию');
      return;
    }
    if (isFemale && !hips) {
      alert('Для женщин нужен также обхват бёдер');
      return;
    }
    // Частая ошибка: в «бёдра» вписывают обхват ноги — формула тогда врёт в разы
    if (isFemale && Number(hips) < Number(waist) * 0.8) {
      alert('Проверь обхват бёдер: мерить нужно таз в самом широком месте (через ягодицы), а не ногу');
      return;
    }
    setSaving(true);
    try {
      const bf = calculateBodyFatNavy({
        sex: profile.sex as 'male' | 'female',
        height_cm: profile.height_cm ?? 170,
        neck_cm: Number(neck),
        waist_cm: Number(waist),
        hips_cm: hips ? Number(hips) : undefined,
      });
      await addMeasurement(profile.id, {
        neck_cm: Number(neck),
        waist_cm: Number(waist),
        hips_cm: hips ? Number(hips) : undefined,
        glutes_cm: glutes ? Number(glutes) : undefined,
        body_fat_percent: bf ?? undefined,
      });
      setNeck('');
      setWaist('');
      setHips('');
      setGlutes('');
      setShowForm(false);
      refetch();
      haptic('success');
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Не удалось сохранить замеры');
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="card p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Ruler size={16} className="text-emerald-glow" />
          <span className="font-display font-semibold">Замеры тела</span>
        </div>
        {currentBf !== null && (
          <div className="px-2 py-0.5 rounded-full bg-emerald-glow/15 text-emerald-glow text-xs font-medium">
            {currentBf}% жира
          </div>
        )}
      </div>
      {latest ? (
        <div className="space-y-1.5 mb-3 text-xs">
          <div className="text-ink-dim">
            Последний замер {format(new Date(latest.measured_at), 'd MMM yyyy', { locale: ru })}
            {lastDays !== null && lastDays > 0 && ` (${lastDays} ${lastDays === 1 ? 'день' : lastDays < 5 ? 'дня' : 'дней'} назад)`}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-ink-muted">
            {latest.neck_cm && <div>Шея: <span className="text-ink">{latest.neck_cm}см</span></div>}
            {latest.waist_cm && <div>Талия: <span className="text-ink">{latest.waist_cm}см</span></div>}
            {latest.hips_cm && <div>Бёдра: <span className="text-ink">{latest.hips_cm}см</span></div>}
            {latest.glutes_cm && <div>Ягодицы: <span className="text-ink">{latest.glutes_cm}см</span></div>}
          </div>
        </div>
      ) : (
        <p className="text-xs text-ink-dim mb-3">
          Замеры помогут точно отслеживать прогресс — формула U.S. Navy рассчитывает % жира.
        </p>
      )}
      {showForm ? (
        <div className="border-t border-line pt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-ink-dim block mb-1">Шея, см</label>
              <input type="number" inputMode="decimal" step="0.5" placeholder="40" className="input text-sm py-2" value={neck} onChange={(e) => setNeck(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] text-ink-dim block mb-1">Талия, см</label>
              <input type="number" inputMode="decimal" step="0.5" placeholder="80" className="input text-sm py-2" value={waist} onChange={(e) => setWaist(e.target.value)} />
            </div>
            {isFemale && (
              <div>
                <label className="text-[10px] text-ink-dim block mb-1">Бёдра (широкая часть), см*</label>
                <input type="number" inputMode="decimal" step="0.5" placeholder="95" className="input text-sm py-2" value={hips} onChange={(e) => setHips(e.target.value)} />
              </div>
            )}
            <div>
              <label className="text-[10px] text-ink-dim block mb-1">Ягодицы, см</label>
              <input type="number" inputMode="decimal" step="0.5" placeholder="100" className="input text-sm py-2" value={glutes} onChange={(e) => setGlutes(e.target.value)} />
            </div>
          </div>
          {previewBf !== null && (
            <div className="bg-emerald-glow/10 border border-emerald-glow/30 rounded-lg p-2 text-center">
              <div className="text-[10px] text-ink-dim">Расчётный % жира (Navy)</div>
              <div className="font-display text-xl font-bold text-emerald-glow">{previewBf}%</div>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted">
              Отмена
            </button>
            <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-lg bg-emerald-glow text-bg text-xs font-bold disabled:opacity-50">
              {saving ? 'Сохраняем…' : 'Сохранить замеры'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => canAdd && setShowForm(true)}
          disabled={!canAdd}
          className={`w-full py-2 rounded-lg text-xs font-medium transition-colors ${
            canAdd ? 'bg-emerald-glow/10 text-emerald-glow hover:bg-emerald-glow/20' : 'bg-bg-elevated text-ink-dim cursor-not-allowed'
          }`}
        >
          {canAdd ? (
            <>+ Внести замеры {!latest && '(первый раз)'}</>
          ) : (
            <>Следующий замер через {7 - (lastDays ?? 0)} {7 - (lastDays ?? 0) === 1 ? 'день' : 'дней'}</>
          )}
        </button>
      )}
      <p className="text-[10px] text-ink-dim mt-2 leading-relaxed">
        💡 Замеряй утром натощак, шею — в самой узкой части, талию — на уровне пупка{isFemale && ', бёдра — по самой широкой части таза, через ягодицы (не обхват ноги)'}.
      </p>
    </div>
  );
}
function SupportCard() {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!profile) return null;
  async function sendMessage() {
    if (!subject.trim() || !message.trim()) {
      setError('Заполни тему и сообщение');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-support`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || anonKey}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({
            userId: profile!.id,
            subject: subject.trim(),
            message: message.trim(),
            contact: contact.trim() || undefined,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Не удалось отправить');
      }
      setSent(true);
      haptic('success');
    } catch (e: any) {
      console.error('Support error:', e);
      setError(e.message || 'Ошибка отправки');
      haptic('error');
    } finally {
      setSending(false);
    }
  }
  function reset() {
    setOpen(false);
    setSent(false);
    setSubject('');
    setMessage('');
    setContact('');
    setError(null);
  }
  if (!open) {
    return (
      <div className="mb-5 space-y-2">
        <a
          href="https://t.me/SLAVAHEALTNEWS"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full card p-4 flex items-center gap-3 hover:bg-bg-elevated transition-colors active:scale-[0.98]"
        >
          <div className="w-9 h-9 rounded-xl bg-sky-glow/15 flex items-center justify-center shrink-0">
            <span className="text-base">{'📢'}</span>
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-semibold text-sm">Новости и обновления</div>
            <div className="text-xs text-ink-dim">t.me/SLAVAHEALTNEWS</div>
          </div>
          <span className="text-ink-dim text-lg">&#8594;</span>
        </a>
        <button
          onClick={() => { setOpen(true); haptic('light'); }}
          className="w-full card p-4 flex items-center gap-3 hover:bg-bg-elevated transition-colors active:scale-[0.98]"
        >
          <div className="w-9 h-9 rounded-xl bg-emerald-glow/15 flex items-center justify-center shrink-0">
            <span className="text-base">{'💬'}</span>
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-semibold text-sm">Поддержка</div>
            <div className="text-xs text-ink-dim">Вопрос, проблема, идея — напиши нам</div>
          </div>
          <span className="text-ink-dim text-lg">&#8594;</span>
        </button>
      </div>
    );
  }
  if (sent) {
    return (
      <div className="card-elevated border-emerald-glow/30 p-4 mb-5 text-center">
        <div className="text-4xl mb-2">{'📨'}</div>
        <div className="font-display font-bold text-base mb-2">Спасибо!</div>
        <p className="text-sm text-ink-muted leading-relaxed mb-3">
          Получил твоё сообщение. Отвечу в течение 24 часов
          {contact ? ` на ${contact}` : profile.tg_username ? ' в Telegram' : ''}.
        </p>
        <button onClick={reset} className="text-xs text-emerald-glow hover:underline">
          Закрыть
        </button>
      </div>
    );
  }
  return (
    <div className="card p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{'💬'}</span>
          <span className="font-display font-semibold">Поддержка</span>
        </div>
        <button onClick={reset} className="text-ink-dim text-sm">✕</button>
      </div>
      <p className="text-xs text-ink-muted mb-3 leading-relaxed">
        Опиши проблему или вопрос — отвечу как можно скорее.
      </p>
      <div className="space-y-3">
        <div>
          <label className="text-[11px] text-ink-dim block mb-1">Тема</label>
          <input
            type="text"
            placeholder="Например: не работает чат"
            className="input text-sm"
            value={subject}
            onChange={(e) => { setSubject(e.target.value); setError(null); }}
            maxLength={200}
            disabled={sending}
          />
        </div>
        <div>
          <label className="text-[11px] text-ink-dim block mb-1">Сообщение</label>
          <textarea
            placeholder="Опиши подробно — что случилось, когда, на каком экране…"
            className="input text-sm min-h-[100px] resize-none"
            value={message}
            onChange={(e) => { setMessage(e.target.value); setError(null); }}
            maxLength={5000}
            disabled={sending}
          />
          <div className="text-[10px] text-ink-dim text-right mt-1">
            {message.length}/5000
          </div>
        </div>
        <div>
          <label className="text-[11px] text-ink-dim block mb-1">
            Контакт для ответа (опционально)
          </label>
          <input
            type="text"
            placeholder="email или @telegram_ник"
            className="input text-sm"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            maxLength={200}
            disabled={sending}
          />
          <p className="text-[10px] text-ink-dim mt-1">
            Если не укажешь — отвечу через твой Telegram аккаунт
          </p>
        </div>
        {error && (
          <div className="bg-danger/10 border border-danger/30 rounded-lg p-2.5 text-xs text-danger">
            {error}
          </div>
        )}
        <button
          onClick={sendMessage}
          disabled={sending || !subject.trim() || !message.trim()}
          className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
        >
          {sending ? 'Отправляем…' : '📤 Отправить'}
        </button>
      </div>
    </div>
  );
}
function ActivityCard() {
  const { profile } = useUserStore();
  const { activities, totalCalories, refetch } = useTodayActivity();
  const { haptic } = useTelegram();
  const [showForm, setShowForm] = useState(false);
  const [activityType, setActivityType] = useState('');
  const [duration, setDuration] = useState('');
  const [calories, setCalories] = useState('');
  const [saving, setSaving] = useState(false);
  const [milestoneData, setMilestoneData] = useState<{ milestone: number; target: number } | null>(null);
  // 🆕 Выбранная дата для активности
  const [activityDate, setActivityDate] = useState<DateOption>('today');
  // 🆕 Редактирование тренировки
  const [editingActivity, setEditingActivity] = useState<ActivityEntry | null>(null);
  if (!profile) return null;
  async function save() {
    if (!activityType.trim() || !calories) {
      alert('Введи название и калории');
      return;
    }
    const cal = Number(calories);
    if (!cal || cal < 1 || cal > 5000) {
      alert('Калории должны быть от 1 до 5000');
      return;
    }
    setSaving(true);
    try {
      // 🆕 Готовим ISO timestamp для выбранной даты
      // Берём 12:00 дня — чтобы не было проблем с часовыми поясами
      const dateStr = dateOptionToString(activityDate);
      const loggedAt = `${dateStr}T12:00:00`;
      
      const result = await addActivity(profile!.id, {
        activity_type: activityType.trim(),
        duration_minutes: duration ? Number(duration) : undefined,
        calories_burned: cal,
        logged_at: activityDate === 'today' ? undefined : loggedAt,  // для сегодня — пусть БД ставит now()
      });
      setActivityType('');
      setDuration('');
      setCalories('');
      setShowForm(false);
      setActivityDate('today');
      refetch();
      haptic('success');
      
      if (result?.new_milestone) {
        setMilestoneData({
          milestone: result.new_milestone,
          target: result.target_value,
        });
      }
    } catch (e) {
      console.error(e);
      alert('Не удалось сохранить');
      haptic('error');
    } finally {
      setSaving(false);
    }
  }
  async function remove(id: string) {
    if (!confirm('Удалить эту активность?')) return;
    try {
      await deleteActivity(id);
      refetch();
      haptic('medium');
    } catch (e) {
      haptic('error');
    }
  }
  const quickPresets = [
    { name: 'Велосипед', cal: 400, dur: 60, emoji: '🚴' },
    { name: 'Бег', cal: 500, dur: 30, emoji: '🏃' },
    { name: 'Зал', cal: 350, dur: 60, emoji: '💪' },
    { name: 'Плавание', cal: 450, dur: 45, emoji: '🏊' },
    { name: 'Йога', cal: 200, dur: 60, emoji: '🧘' },
    { name: 'Прогулка', cal: 150, dur: 60, emoji: '🚶' },
  ];
  function applyPreset(p: typeof quickPresets[0]) {
    setActivityType(p.name);
    setCalories(String(p.cal));
    setDuration(String(p.dur));
  }
  return (
    <div className="card p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Dumbbell size={16} className="text-warn" />
          <span className="font-display font-semibold">Активность сегодня</span>
        </div>
        {totalCalories > 0 && (
          <div className="px-2 py-0.5 rounded-full bg-warn/15 text-warn text-xs font-medium">
            −{totalCalories} ккал
          </div>
        )}
      </div>
      {activities.length > 0 ? (
        <div className="space-y-2 mb-3">
          {activities.map((a) => (
  <div key={a.id} className="flex items-center gap-2 bg-bg-elevated rounded-lg px-3 py-2 group">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium truncate">{a.activity_type}</div>
      <div className="text-[10px] text-ink-dim">
        {a.duration_minutes ? `${a.duration_minutes} мин · ` : ''}
        −{a.calories_burned} ккал
      </div>
    </div>
    <button
      onClick={() => {
        setEditingActivity(a);
        haptic('light');
      }}
      className="text-ink-dim hover:text-emerald-glow transition-colors p-1.5 rounded-md hover:bg-emerald-glow/10"
      aria-label="Редактировать"
    >
      <Pencil size={14} />
    </button>
    <button
      onClick={() => remove(a.id)}
      className="text-ink-dim hover:text-danger transition-colors p-1.5 rounded-md hover:bg-danger/10"
      aria-label="Удалить"
    >
      <Trash2 size={14} />
    </button>
  </div>
))}
        </div>
      ) : (
        <p className="text-xs text-ink-dim mb-3">
          Запиши свои тренировки, прогулки или спорт.<br/>
          Калории расхода учитываются автоматически.
        </p>
      )}
      {showForm ? (
        <div className="border-t border-line pt-3 space-y-2">
          {/* 🆕 Переключатель даты */}
          <div>
            <div className="text-[10px] text-ink-dim mb-1.5">📅 За какой день?</div>
            <DateSelector value={activityDate} onChange={setActivityDate} />
          </div>
          
          <div>
            <div className="text-[10px] text-ink-dim mb-1.5">Быстрый выбор:</div>
            <div className="grid grid-cols-3 gap-1.5">
              {quickPresets.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className="text-xs py-1.5 rounded-lg bg-bg-elevated hover:bg-emerald-glow/10 hover:text-emerald-glow transition-colors flex items-center justify-center gap-1"
                >
                  <span>{p.emoji}</span>
                  <span>{p.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-ink-dim block mb-1">Вид активности</label>
            <input
              type="text"
              placeholder="Например: велосипед, бег, зал"
              className="input text-sm py-2"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-ink-dim block mb-1">Длительность, мин</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="60"
                className="input text-sm py-2"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </div>
            <div>
              <label className="text-[10px] text-ink-dim block mb-1">Сожжённые ккал *</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="350"
                className="input text-sm py-2"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
              />
            </div>
          </div>
          <p className="text-[10px] text-ink-dim">
            💡 Если есть фитнес-трекер — впиши точное значение оттуда
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => { setShowForm(false); setActivityDate('today'); }}
              className="flex-1 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted"
            >
              Отмена
            </button>
            <button
              onClick={save}
              disabled={saving || !activityType.trim() || !calories}
              className="flex-1 py-2 rounded-lg bg-emerald-glow text-bg text-xs font-bold disabled:opacity-50"
            >
              {saving ? 'Сохраняем…' : 'Записать'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full py-2 rounded-lg bg-warn/10 text-warn text-xs font-medium hover:bg-warn/20 transition-colors flex items-center justify-center gap-1.5"
        >
          + Добавить активность
        </button>
      )}
      <p className="text-[10px] text-ink-dim mt-2 leading-relaxed">
        💬 Можешь сказать Дофи: «катался на велосипеде час, 500 ккал»
      </p>
      {/* Модалка ачивки */}
      {milestoneData && (
        <AchievementModal
          open={!!milestoneData}
          onClose={() => setMilestoneData(null)}
          milestone={milestoneData.milestone}
          target={milestoneData.target}
          goalType="workouts_year"
        />
      )}
      
      {/* 🆕 Модалка редактирования тренировки */}
      <EditActivityEntryModal
        open={!!editingActivity}
        activity={editingActivity}
        onClose={() => setEditingActivity(null)}
        onSaved={() => {
          refetch();
          setEditingActivity(null);
        }}
      />
    </div>
  );
}
function BMRCard({ profile }: { profile: any }) {
  const { setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [customBmrInput, setCustomBmrInput] = useState('');
  const [bmrSourceInput, setBmrSourceInput] = useState('');
  const [saving, setSaving] = useState(false);
  
  if (!profile?.sex || !profile?.weight_kg || !profile?.height_cm || !profile?.birth_date) {
    return null;
  }
  
  const calculatedBmr = calculateBMR({
    sex: profile.sex,
    weightKg: profile.weight_kg,
    heightCm: profile.height_cm,
    ageYears: ageFromBirthDate(profile.birth_date),
  });
  
  const activeBmr = profile.use_custom_bmr && profile.custom_bmr 
    ? profile.custom_bmr 
    : calculatedBmr;
  
  const isCustom = !!profile.use_custom_bmr && !!profile.custom_bmr;
  
  function startEdit() {
    setCustomBmrInput(String(profile.custom_bmr ?? calculatedBmr));
    setBmrSourceInput(profile.bmr_source ?? '');
    setEditing(true);
  }
  
  function cancelEdit() {
    setEditing(false);
    setCustomBmrInput('');
    setBmrSourceInput('');
  }
  
  async function saveCustomBmr() {
    const value = Number(customBmrInput);
    if (!value || value < 800 || value > 3500) {
      alert('BMR должен быть от 800 до 3500 ккал/день');
      return;
    }
    if (!bmrSourceInput) {
      alert('Укажи откуда у тебя это значение');
      return;
    }
    
    // Проверка на сильное отклонение от формулы
    const deviation = Math.abs(value - calculatedBmr) / calculatedBmr;
    if (deviation > 0.3) {
      const confirmMsg = `Твоё значение ${value} ккал отличается от расчётного (${calculatedBmr}) более чем на 30%. Это серьёзное отклонение. Точно сохранить?`;
      if (!confirm(confirmMsg)) return;
    }
    
    setSaving(true);
    try {
      if (!(await requireSession())) return;
      // Обновляем custom_bmr + пересчитываем дневные нормы
      const newTargets = buildDailyTargets({
        sex: profile.sex,
        birth_date: profile.birth_date,
        height_cm: profile.height_cm,
        weight_kg: profile.weight_kg,
        goal: profile.goal,
        activity_level: profile.activity_level,
        custom_bmr: value,
        use_custom_bmr: true,
      });
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          custom_bmr: value,
          use_custom_bmr: true,
          bmr_source: bmrSourceInput,
          ...newTargets,
        })
        .eq('id', profile.id)
        .select()
        .single();
      
      if (error) throw error;
      
      setProfile(data);
      setEditing(false);
      haptic('success');
    } catch (e) {
      console.error('[BMRCard] save error:', e);
      haptic('error');
      alert('Не удалось сохранить. Попробуй ещё раз.');
    } finally {
      setSaving(false);
    }
  }
  
  async function resetToFormula() {
    if (!confirm('Вернуть расчёт по формуле Mifflin-St Jeor? Дневные нормы будут пересчитаны.')) return;
    
    setSaving(true);
    try {
      if (!(await requireSession())) return;
      const newTargets = buildDailyTargets({
        sex: profile.sex,
        birth_date: profile.birth_date,
        height_cm: profile.height_cm,
        weight_kg: profile.weight_kg,
        goal: profile.goal,
        activity_level: profile.activity_level,
        custom_bmr: null,
        use_custom_bmr: false,
      });
      
      const { data, error } = await supabase
        .from('profiles')
        .update({
          use_custom_bmr: false,
          custom_bmr: null,
          bmr_source: null,
          ...newTargets,
        })
        .eq('id', profile.id)
        .select()
        .single();
      
      if (error) throw error;
      
      setProfile(data);
      setEditing(false);
      haptic('success');
    } catch (e) {
      console.error('[BMRCard] reset error:', e);
      haptic('error');
    } finally {
      setSaving(false);
    }
  }
  
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center gap-3 hover:bg-bg-elevated/50 transition-colors"
      >
        <span className="text-base">🔥</span>
        <span className="text-sm text-ink-muted flex-1 text-left">Базальный метаболизм</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-emerald-glow">{activeBmr} ккал/день</span>
          {isCustom && (
            <span className="text-[9px] bg-sky-glow/20 text-sky-glow px-1.5 py-0.5 rounded-full">
              ручной
            </span>
          )}
        </div>
        <span className={`text-ink-dim text-xs transition-transform ${expanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-line/30 bg-bg-elevated/30">
          {!editing ? (
            <>
              <p className="text-[11px] text-ink-muted leading-relaxed mb-2">
                Сколько калорий твоё тело сжигает <span className="text-ink">в покое</span> — 
                на дыхание, кровообращение, работу органов.
              </p>
              <p className="text-[11px] text-ink-muted leading-relaxed mb-2">
                Общий расход за день = базальный метаболизм + шаги + тренировки.
              </p>
              
              {isCustom ? (
                <>
                  <div className="bg-sky-glow/10 border border-sky-glow/30 rounded-lg p-2.5 mb-3 mt-3">
                    <div className="text-[10px] text-ink-dim mb-1">Используется ручной BMR</div>
                    <div className="font-display text-base font-bold text-sky-glow">
                      {profile.custom_bmr} ккал/день
                    </div>
                    <div className="text-[10px] text-ink-muted mt-1">
                      Источник: {{
                        lab_test: 'лабораторный замер',
                        body_composition: 'анализ состава тела',
                        custom: 'другая формула',
                      }[profile.bmr_source ?? ''] ?? 'не указан'}
                    </div>
                    <div className="text-[10px] text-ink-dim mt-1">
                      По формуле было бы: {calculatedBmr} ккал/день
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={startEdit}
                      className="flex-1 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted hover:text-ink"
                    >
                      Изменить
                    </button>
                    <button
                      onClick={resetToFormula}
                      disabled={saving}
                      className="flex-1 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted hover:text-ink"
                    >
                      Вернуть формулу
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[10px] text-ink-dim leading-relaxed mt-2 mb-3">
                    💡 Расчёт по формуле <span className="text-ink-muted">Mifflin-St Jeor</span>. 
                    Реальный показатель может отличаться на ±10-15% в зависимости от особенностей 
                    организма (состав тела, гормональный фон, метаболическая адаптация).
                  </p>
                  <button
                    onClick={startEdit}
                    className="w-full py-2 rounded-lg bg-emerald-glow/10 text-emerald-glow text-xs font-medium hover:bg-emerald-glow/20"
                  >
                    ⚙️ Установить вручную (для опытных)
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {/* Дисклеймер */}
              <div className="bg-warn/10 border border-warn/30 rounded-lg p-2.5 mb-3">
                <div className="text-[11px] text-warn font-medium mb-1">⚠️ Только для опытных</div>
                <p className="text-[10px] text-ink-muted leading-relaxed">
                  Меняй BMR только если ты знаешь своё значение из <strong className="text-ink">лаборатории</strong> (непрямая 
                  калориметрия) или <strong className="text-ink">анализа состава тела</strong> (InBody, DEXA).
                </p>
                <p className="text-[10px] text-ink-muted leading-relaxed mt-1.5">
                  <strong className="text-warn">Не вводи число «на глаз»</strong> — неправильный BMR 
                  испортит расчёт калорий и БЖУ.
                </p>
              </div>
              
              {/* Текущий по формуле */}
              <div className="bg-bg-elevated rounded-lg p-2.5 mb-3">
                <div className="text-[10px] text-ink-dim mb-1">По формуле (для сравнения)</div>
                <div className="font-display text-base font-bold">
                  {calculatedBmr} <span className="text-xs text-ink-dim">ккал/день</span>
                </div>
              </div>
              
              {/* Ввод BMR */}
              <div className="mb-3">
                <label className="text-[10px] text-ink-dim block mb-1">
                  Твой BMR (ккал/день)
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="Например: 1650"
                  className="input text-sm py-2"
                  value={customBmrInput}
                  onChange={(e) => setCustomBmrInput(e.target.value)}
                  min={800}
                  max={3500}
                />
                <div className="text-[10px] text-ink-dim mt-1">
                  Допустимо: 800–3500 ккал/день
                </div>
              </div>
              
              {/* Источник */}
              <div className="mb-3">
                <label className="text-[10px] text-ink-dim block mb-1">
                  Откуда значение?
                </label>
                <select
                  className="input text-sm py-2"
                  value={bmrSourceInput}
                  onChange={(e) => setBmrSourceInput(e.target.value)}
                >
                  <option value="">— Выбери источник —</option>
                  <option value="lab_test">Лабораторный замер (калориметрия)</option>
                  <option value="body_composition">Анализ состава тела (InBody, DEXA)</option>
                  <option value="custom">Расчёт по другой формуле</option>
                </select>
              </div>
              
              {/* Кнопки */}
              <div className="flex gap-2">
                <button
                  onClick={cancelEdit}
                  disabled={saving}
                  className="flex-1 py-2 rounded-lg bg-bg-elevated text-xs text-ink-muted"
                >
                  Отмена
                </button>
                <button
                  onClick={saveCustomBmr}
                  disabled={saving || !customBmrInput || !bmrSourceInput}
                  className="flex-1 py-2 rounded-lg bg-emerald-glow text-bg text-xs font-bold disabled:opacity-50"
                >
                  {saving ? 'Сохраняем…' : 'Применить'}
                </button>
              </div>
              
              <p className="text-[10px] text-ink-dim text-center mt-2">
                После применения пересчитаются калории и БЖУ
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
